import React, { useEffect, useState } from "react";
import { Volume2 } from "lucide-react";
import { BPM, getAudioCtx, startDiscoBeat } from "./discoBeat";

const DANCERS = ["dancer_team_1", "dancer_team_2", "dancer_team_3", "dancer_team_4"];
const SEQUENCE = ["y", "m", "c", "a", "a", "", "", ""];
const BEAT_MS = 60000 / BPM;
const src = (d, p) => `/totem/${d}${p ? `_${p}` : ""}.png`;

const POSES = ["", "y", "m", "c", "a"];

function useMusic(musicUrl) {
  const [blocked, setBlocked] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (musicUrl) {
      const a = new Audio(musicUrl);
      a.loop = true;
      a.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
      return () => { a.pause(); a.removeAttribute("src"); };
    }
    const ctx = getAudioCtx();
    if (!ctx) return undefined;
    let cancelled = false;
    let stopBeat = null;
    const go = () => { if (!cancelled && !stopBeat) { setBlocked(false); stopBeat = startDiscoBeat(); } };
    if (ctx.state === "running") go();
    else ctx.resume().then(() => { if (ctx.state === "running") go(); }).catch(() => {});
    const t = setTimeout(() => { if (!cancelled && ctx.state !== "running") setBlocked(true); }, 500);
    return () => { cancelled = true; clearTimeout(t); if (stopBeat) stopBeat(); };
  }, [musicUrl, tick]);
  const unlock = async () => {
    const ctx = getAudioCtx();
    if (ctx) await ctx.resume().catch(() => {});
    setTick((t) => t + 1);
  };
  return { blocked, unlock };
}

export function DancingCrew({ musicUrl }) {
  const [beat, setBeat] = useState(0);
  const { blocked, unlock } = useMusic(musicUrl);
  useEffect(() => {
    const iv = setInterval(() => setBeat((b) => b + 1), BEAT_MS);
    return () => clearInterval(iv);
  }, []);
  const pose = SEQUENCE[beat % SEQUENCE.length];
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex items-end justify-center gap-[2vw] px-4" data-testid="totem-dancers" data-pose={pose || "base"}>
      <style>{`
        @keyframes totem-enter { from { transform: translateY(120%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes totem-hop-0 { 0% { transform: translateY(0) scale(1,1); } 30% { transform: translateY(-6%) scale(0.98,1.03); } 60% { transform: translateY(0) scale(1.03,0.97); } 100% { transform: translateY(0) scale(1,1); } }
        @keyframes totem-hop-1 { 0% { transform: translateY(0) scale(1,1); } 30% { transform: translateY(-6%) scale(0.98,1.03); } 60% { transform: translateY(0) scale(1.03,0.97); } 100% { transform: translateY(0) scale(1,1); } }
        @keyframes totem-sway { 0%,100% { transform: rotate(-4deg); } 50% { transform: rotate(4deg); } }
      `}</style>
      {DANCERS.map((d, i) => (
        <div key={d} style={{ animation: `totem-enter 0.7s ease-out ${i * 0.15}s both` }}>
          <div style={{ animation: pose ? "none" : `totem-sway ${BEAT_MS * 2}ms ease-in-out infinite`, transformOrigin: "bottom center" }}>
            <div
              className="relative grid"
              style={{ animation: `totem-hop-${beat % 2} ${BEAT_MS}ms ease-out`, transformOrigin: "bottom center" }}
              data-testid={`totem-dancer-${i}`}
            >
              {POSES.map((p) => (
                <img
                  key={p}
                  src={src(d, p)}
                  alt=""
                  className="col-start-1 row-start-1 h-[34vh] max-h-[360px] w-auto self-end justify-self-center drop-shadow-[0_10px_12px_rgba(0,0,0,0.25)]"
                  style={{ visibility: p === pose ? "visible" : "hidden" }}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
      {blocked && (
        <button
          type="button"
          onClick={unlock}
          className="pointer-events-auto absolute right-4 top-[-3.5rem] inline-flex items-center gap-2 rounded-full bg-[#0052FF] px-4 py-2 text-sm font-bold text-white shadow-lg transition-transform hover:scale-105"
          data-testid="totem-music-unlock-btn"
        >
          <Volume2 className="h-4 w-4" /> Attiva musica
        </button>
      )}
    </div>
  );
}
